import { SearchService } from "./search.service";
import { EntityData } from "../entity/entity.data";
import { ReplaySubject } from "rxjs/ReplaySubject";
import { EventEmitter, Output, ReflectiveInjector } from "@angular/core";
import { LocalisableEntityData } from "../entity/localisable-entity.data";
import { AbstractSearchModel, FilterValueChangedEvent } from "./abstract-search.model";
/**
 * Provides a search model and its dependencies
 * @param modelType
 * @returns {any[]}
 */
export function provideSearchModel(modelType) {
    return [
        modelType,
        { provide: SearchModel, useExisting: modelType },
        { provide: AbstractSearchModel, useExisting: modelType }
    ];
}
export class SearchResult {
    constructor(data, total, countable) {
        this.data = data;
        this.totalCount = total;
        this.countable = countable;
    }
}
/**
 * Base class for search entity models. A model represents the resource state, as well as provides operations
 * that can be performed on the resource.
 *
 * A default service implementation is provided for performing server side communication. More specific implementations
 * may be provided via the subclass constructor if required.
 *
 * Generic parameter T defines the type of a array of EntityData to be held by the model. In case a EntityData is not required, the any can be used.
 *
 * Created by Sergio Gomes
 */
export class SearchModel extends AbstractSearchModel {
    /**
     * Constructs with the service which is used for all server side comms.
     *
     * This constructor must be replicated in subclasses to allow Angular dependency injection to provide the service.
     * Subclasses may also specify a more specialised service that extends EntityService. If so, then the subclass
     * must also override #getEntityService
     *
     * @param injector Injector
     * @param dataType EntityData subclass
     * @param resourceVersion Resource version
     */
    constructor(injector, dataType) {
        super();
        this.injector = injector;
        this.dataType = dataType;
        /**
         * Can be subscribed to get the search data when it is resolved
         * @type {ReplaySubject<any>}
         * @private
         */
        this._$data = new ReplaySubject(1);
        this.$data = this._$data.asObservable();
        /**
         * Define if the model is pageable
         */
        this.pageable = true;
        /**
         * Define the filter for the search
         * @type {{}}
         */
        this._internalFilter = {};
        this.dataViewChange = new EventEmitter();
        if (injector == null) {
            console.error("!!!! Injector not provided. This is most likely due to a missing constructor in " + this.constructor.toString());
        }
        this.createService();
        this.data = [];
    }
    createService() {
        const reflectInjector = ReflectiveInjector.resolveAndCreate([
            { provide: SearchService.SEARCH_RESOURCE, useValue: this.getResourceName() },
            { provide: SearchService.RESOURCE_VERSION, useValue: this.getResourceVersion() },
            this.getSearchService()
        ], this.injector);
        this.service = reflectInjector.get(this.getSearchService());
    }
    getNameSpace() {
        return this.service.getNameSpace();
    }
    getResource() {
        return this.service.getResource();
    }
    /**
     * Retrieves the name of the model
     * @returns {string}
     */
    getModelName() {
        return this.constructor.name;
    }
    /**
     * Retrieves the kebab case name of the model which is in a format that matches the naming convention of
     * the server side resource
     * @returns {string} Name of this model
     */
    getResourceName() {
        var s = this.getModelName();
        if (s.endsWith("Model")) {
            s = s.substr(0, s.length - "Model".length);
        }
        if (s.endsWith("Search")) {
            s = s.substr(0, s.length - "Search".length);
        }
        s = s.replace(/\.?([A-Z])/g, function (x, y) {
            return "-" + y.toLowerCase();
        }).replace(/^-/, "");
        return s;
    }
    /**
     * Method to be override from the children in case the search results are not entity data
     * @returns {null}
     */
    getIdentifierField() {
        return null;
    }
    getIdentifier(item) {
        let identifierField = this.getIdentifierField();
        if (!identifierField) {
            if (item instanceof EntityData) {
                identifierField = item.getIdentifierKey();
            }
        }
        if (!identifierField) {
            throw new Error("Identifier field not defined.");
        }
        return item[identifierField];
    }
    /**
     * Helper method to allow framework components to check for discontinued search result items.
     * The default behaviour is to return the value of a "discontinued" field on the item.
     * This method may be overridden by search model implementations, however for search results mapped to EntityData classes,
     * the preferred approach is to provide an appropriate getter on the EntityData class itself if required.
     * @param item A search result item.
     * @return true if the item is discontinued, false if not, or undefined if the item does not support discontinuation.
     */
    isDiscontinued(item) {
        return item["discontinued"];
    }
    /**
     * Helper method to allow framework components to set search result items as discontinued.
     * The default behaviour is to set the value of a "discontinued" field on the item.
     * This method may be overridden by search model implementations, however for search results mapped to EntityData classes,
     * the preferred approach is to provide an appropriate setter on the EntityData class itself if required.
     * @param item A search result item.
     * @param discontinued
     */
    setDiscontinued(item, discontinued) {
        item["discontinued"] = discontinued;
    }
    /**
     * Returns the service type expected by this model (defaults to a generic SearchService).
     * Extending models should override this if a specialised service is required.
     * @returns {EntityService}
     */
    getSearchService() {
        return SearchService;
    }
    searchRecords(filter, start, limit, sort) {
        let result = new ReplaySubject(1);
        this.service.search(filter, start, limit, sort).subscribe((json) => {
            let data;
            if (this.dataType) {
                data = EntityData.createArray(json.list, this.dataType);
            }
            else {
                data = json.list;
            }
            result.next(new SearchResult(this.postProcessData(data), json.totalCount, json.countable));
            result.complete();
        }, (error) => {
            result.error(error);
            result.complete();
        });
        return result;
    }
    postProcessData(data) {
        return data;
    }
    createSearch() {
        return this.searchRecords(this.filter, this.pageable ? this.start : undefined, this.pageable ? this.limit : undefined, this.sort);
    }
    /**
     * We are subscribing to the observable to capture the data,
     * so we need to wrap the observable in a ReplaySubject so that our caller can also see the result.
     */
    createSearchResultHandler() {
        const s = new ReplaySubject();
        s.subscribe((searchResult) => {
            this.data = searchResult.data;
            this.total = searchResult.totalCount;
            this.countable = searchResult.countable;
            this.dataView = { data: this.data, total: this.total, start: this.start, limit: this.limit, sort: this.sort };
            this.dataViewChange.emit(this.dataView);
            this._$data.next(this.data);
        }, (error) => {
        });
        return s;
    }
    /**
     * Performs the search "asynchronously".
     * The returned observable may never emit any results, as subsequent calls to this method will discard in-flight searches.
     * This is intended for use where the dataViewChange event emitter is subscribed to to retrieve the results independent of the search initiation.
     */
    search() {
        // Stop a running search and restart
        if (this.lastSearchSubscription) {
            this.lastSearchSubscription.unsubscribe();
        }
        // Start the search
        const s = this.createSearchResultHandler();
        this.lastSearchSubscription = this.createSearch().subscribe(s);
        return s;
    }
    /**
     * Performs the search and returns the results via a promise.
     * The promise will always resolve. Concurrent requests will resolve separately.
     */
    retrieve(timeout) {
        const s = this.createSearchResultHandler();
        let o = this.createSearch();
        if (timeout) {
            o = o.timeout(timeout);
        }
        o.take(1).subscribe(s);
        return s.toPromise();
    }
    get filter() {
        return this._internalFilter;
    }
    set filter(value) {
        this._internalFilter = value;
    }
    getFilterValue(filterName) {
        return this._internalFilter[filterName];
    }
    canSearch() {
        return this.service.canSearch();
    }
    /**
     * Set a filter value triggering FilterValueChangedEvent and FilterChange
     * @param filterName
     * @param value
     */
    setFilterValue(filterName, value) {
        if (!this.deepEqual(this.filter[filterName], value)) {
            this.filter[filterName] = value;
            this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, value));
            this.filterChange.emit(this.filter);
        }
    }
    /**
     * Compare two values considering arrays equal when all elements and index are the equal
     * @param value1
     * @param value2
     * @returns {boolean}
     */
    deepEqual(value1, value2) {
        if (Array.isArray(value1) && Array.isArray(value2)) {
            return value1.length !== value2.length ? false : value1.find((i, index) => i !== value2[index]) === undefined;
        }
        else {
            return value1 === value2;
        }
    }
    /**
     * Set filter values using a object iterating through the properties and its values triggering FilterValueChangedEvent and FilterChange
     * @param filter
     */
    setFilterValues(filter) {
        let changed = false;
        for (var filterName in filter) {
            if (filter.hasOwnProperty(filterName)) {
                const value = filter[filterName];
                if (this.filter[filterName] !== value) {
                    this.filter[filterName] = value;
                    this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, value));
                    changed = true;
                }
            }
        }
        if (changed) {
            this.filterChange.emit(this.filter);
        }
    }
    /**
     * Returns if the model is dealing with a localisable entity.
     * When dataType is provided it checks if the type is an LocalisableEntityData to determine if the entity is localisable, otherwise defaults to true
     * @returns {boolean}
     */
    isEntityLocalisable() {
        if (this.dataType) {
            return Object.getPrototypeOf(this.dataType) === LocalisableEntityData;
        }
        else {
            return true;
        }
    }
    /**
     * Returns the version of the resource.
     * @returns {number}
     */
    getResourceVersion() {
        return 0;
    }
}
SearchModel.propDecorators = {
    'dataViewChange': [{ type: Output },],
};
//# sourceMappingURL=search.model.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/resource/search/search.model.js
// module id = 103
// module chunks = 1